import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CKAuthService } from '@ck-core/ck-auth';
import { ToastService } from '@ck-core/ck-controls';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FeedbackService } from '../../services/feedback.service';
import { CkControlsModule } from '@ck-core/ck-controls';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'feedback-popup',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, CkControlsModule, TranslocoDirective],
    templateUrl: './feedback-popup.component.html',
    styleUrl: './feedback-popup.component.scss',
})
export class FeedbackPopupComponent {
    private readonly fb = inject(FormBuilder);
    private readonly feedbackService = inject(FeedbackService);
    private readonly toastService = inject(ToastService);
    private readonly auth = inject(CKAuthService);
    private readonly transloco = inject(TranslocoService);

    public readonly activeModal = inject(NgbActiveModal);

    public readonly emojiItems = [
        { emoji: '😡', key: 'terrible' },
        { emoji: '😕', key: 'bad' },
        { emoji: '😐', key: 'neutral' },
        { emoji: '😊', key: 'good' },
        { emoji: '🤩', key: 'amazing' },
    ];

    public selectedEmoji = '';
    public isSubmitting = false;
    public loading: boolean = false;

    public readonly feedbackTypes = computed(() => [
        { value: 0, label: this.transloco.translate('feedback.types.general') || 'General Feedback' },
        { value: 1, label: this.transloco.translate('feedback.types.bug') || 'Bug Report' },
        { value: 2, label: this.transloco.translate('feedback.types.other') || 'Other' },
    ]);

    form = this.fb.group({
        typeId: ['', Validators.required],
        rating: ['', Validators.required],
        comments: [''],
    });

    selectEmoji(key: string): void {
        this.selectedEmoji = key;
        this.form.patchValue({ rating: key });
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            if (!this.selectedEmoji) {
                this.toastService.error(
                    this.transloco.translate('feedback.ratingRequired') || 'Please select a rating',
                );
            }

            return;
        }

        this.loading = true;
        this.isSubmitting = true;

        this.feedbackService
            .SaveFeedback({
                ...this.form.getRawValue(),
                userId: this.auth.authData?.AspNetUserId,
                tenantId: this.auth.authData?.moreInfo?.tenantId,
                applicationName: 'AST',
                Subject: 'Email Subject',
                toEmails: [{ name: '', email: '' }],
                ccEmails: [{ name: '', email: '' }],
                emailTemplate: '',
            })
            .subscribe({
                next: () => {
                    this.loading = false;
                    this.toastService.success(
                        this.transloco.translate('feedback.success') ||
                            'Thank you for your feedback.',
                    );

                    this.activeModal.close(true);
                },
                error: (err) => {
                    this.loading = false;

                    this.toastService.error(err.message);
                },
            });
    }

    close(): void {
        this.activeModal.dismiss();
    }
}
